/**
 * SupplierApi.ts
 * 협력사 기본 정보 API
 *
 * @description
 * - 협력사 목록 조회, 상세 정보 조회/수정 기능 제공
 * - 협력사 정산 정보, 사업자 정보, 기본 정보 관리
 * - 협력사 등급 엑셀 업로드 기능
 */
import { apiClient } from '@/shared/api/client';
import {
    SupplierListResponseDto,
    SupplierDetailListResponseDto,
    GetSupplierInfoResponseDto,
    UpdateSupplierSettlementRequest,
    UpdateSupplierBusinessInfoRequest,
    UpdateSupplierBasicInfoRequest,
    GetDeductListResponseDto,
} from '../types/supplier.api.types';

/** API 기본 경로 */
const BASE_PATH = '/api/v1/supplier';

/**
 * 협력사 API 서비스
 * 협력사 정보 조회 및 관리를 위한 API 함수들
 */
export const supplierApi = {
    /**
     * 협력사 목록 조회
     * GET /api/v1/supplier/list
     */
    getSupplierList: async (): Promise<SupplierListResponseDto> => {
        const response = await apiClient.get<SupplierListResponseDto>(
            `${BASE_PATH}/list`
        );
        return response.data;
    },

    /**
     * 협력사 상세 목록 조회 (선택 페이지용)
     * GET /api/v1/supplier/list/detail
     */
    getSupplierDetailList: async (params?: {
        supplierName?: string;
        supplierId?: string;
        operationStatus?: string;
    }): Promise<SupplierDetailListResponseDto> => {
        const response = await apiClient.get<SupplierDetailListResponseDto>(
            `${BASE_PATH}/list`,
            { params }
        );
        return response.data;
    },

    /**
     * 협력사 상세 정보 조회
     * GET /api/v1/supplier/info/{suppId}
     */
    getSupplierInfo: async (suppId: string): Promise<GetSupplierInfoResponseDto> => {
        const response = await apiClient.get<GetSupplierInfoResponseDto>(
            `${BASE_PATH}/info/${suppId}`
        );
        return response.data;
    },

    /**
     * 협력사 정산 정보 수정
     * PUT /api/v1/supplier/settlement/{suppId}
     */
    updateSupplierSettlement: async (suppId: string, request: UpdateSupplierSettlementRequest): Promise<void> => {
        await apiClient.put<void>(
            `${BASE_PATH}/settlement/${suppId}`,
            request
        );
    },

    /**
     * 공제 (일차감) 목록 조회
     * GET /api/v1/deduct/list
     * @param suppId 협력사 ID (선택) - 입력 시 해당 협력사의 공제만 조회
     */
    getDeductList: async (suppId?: string): Promise<GetDeductListResponseDto> => {
        const params: Record<string, string> = {};
        if (suppId) {
            params.suppId = suppId;
        }
        const response = await apiClient.get<GetDeductListResponseDto>(
            '/api/v1/deduct/list',
            { params }
        );
        return response.data;
    },

    /**
     * 협력사 등급 엑셀 업로드
     * POST /api/v1/supplier/grade/upload
     */
    uploadGradeExcel: async (file: File, userId: string): Promise<{ success: boolean; message: string }> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('userId', userId);

        const response = await apiClient.post<{ success: boolean; message: string; }>(
            `${BASE_PATH}/grade/upload`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    },

    /**
     * 협력사 사업자 정보 수정
     * PUT /api/v1/supplier/business-info/{suppId}
     */
    updateSupplierBusinessInfo: async (suppId: string, request: UpdateSupplierBusinessInfoRequest): Promise<void> => {
        await apiClient.put<void>(
            `${BASE_PATH}/business-info/${suppId}`,
            request
        );
    },

    /**
     * 협력사 기본 정보 수정 (휴대폰번호, 주소)
     * PUT /api/v1/supplier/basic-info/{suppId}
     */
    updateSupplierBasicInfo: async (suppId: string, request: UpdateSupplierBasicInfoRequest): Promise<void> => {
        await apiClient.put<void>(
            `${BASE_PATH}/basic-info/${suppId}`,
            request
        );
    }
};
